import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useOrganization } from '../contexts/OrganizationContext';
import {
  Box,
  Flex,
  Text,
  Button,
  Card,
  Heading,
  IconButton,
  DropdownMenu,
  Avatar,
  Separator,
  Popover,
  Callout,
  Tooltip,
  AlertDialog,
} from '@radix-ui/themes';
import {
  PlusIcon,
  ExitIcon,
  GearIcon,
  DashboardIcon,
  ClockIcon,
  InfoCircledIcon,
  ComponentPlaceholderIcon,
  Cross2Icon,
  PersonIcon,
  FileTextIcon,
} from '@radix-ui/react-icons';
import { useAuth } from '../contexts/AuthContext';
import { useClinicAdmin } from '../hooks/useClinicAdmin';
import { useSlowConnection } from '../hooks/useSlowConnection';
import { useCustomViewsAccess } from './customViews/useCustomViews';
import PatientList from './PatientList';
import OnboardingWorkflow from './OnboardingWorkflow';
import { toast } from 'react-hot-toast';
import OrganizationSelector from './OrganizationSelector';
import { SmartHubPanel } from './SmartHub';
import { patientApi } from '../services/api';
import {
  usePatientTabs,
  type DashboardTabId,
} from './PatientTabs/PatientTabsContext';
import PatientTabContent from './PatientTabs/PatientTabContent';
import { PATIENT_DETAIL_TAB_INDEX } from './PatientDetailTabIndex';
import {
  REGENERATE_PATIENT_ID_STORAGE_KEY,
  REGENERATE_SOURCE_STORAGE_KEY,
} from '../utils/carePlanRefreshSource';
import { APP_HEADER_HEIGHT } from './dashboardLayout';
import { FLOATING_CHAT_CLEARANCE } from './chat/floatingChatGeometry';

// PHA-509: Dashboard now hosts a dynamic strip of tabs:
//   [Patient List] [Patient A ×] [Patient B ×]
// plus a hidden Onboarding state activated via ?tab=onboarding. Patient List is
// always present and non-closable; clicking a row in PatientList opens or
// focuses a patient tab via the PatientTabs context.

const DashboardLayout: React.FC = () => {
  const { user, logout } = useAuth();
  const { orgId: currentOrgId, orgList, setOrg, loading: orgLoading } = useOrganization();

  // PHA-1031: clinics that don't collect data through Phanova (ODA) hide the Smart
  // Hub rail entirely. Keyed on the org-switcher's orgId, never on mount alone —
  // Dashboard stays mounted across a clinic switch, and a `[]`-dep fetch would keep
  // answering for the previous clinic (PHA-959).
  //
  // Three states, and the null matters: `null` = not answered yet, and the rail is
  // NOT rendered while unknown. Starting at `false` would render the rail and then
  // yank it away a round-trip later for exactly the clinics meant not to have one —
  // and would mount SmartHubPanel long enough to fire its feed calls for them.
  // Everything that isn't a real answer still lands on `false` (rail visible): a
  // fetch error, and org ids we can't ask about ('' before a selection, 'ALL' in the
  // internal all-clinics view). Unlike PHA-866's target dates, a briefly delayed rail
  // is cosmetic, not a compliance leak, so gating on `null` is enough here.
  const [hideSmartHub, setHideSmartHub] = useState<boolean | null>(null);
  useEffect(() => {
    if (orgLoading) return;
    if (!currentOrgId || currentOrgId === 'ALL') {
      setHideSmartHub(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const result = await patientApi.getOrgHideSmartHubEnabled(currentOrgId);
        if (!cancelled) setHideSmartHub(result?.hide_smart_hub_enabled === true);
      } catch {
        if (!cancelled) setHideSmartHub(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentOrgId, orgLoading]);
  const [orgPickerOpen, setOrgPickerOpen] = useState(false);
  const { isClinicAdmin } = useClinicAdmin();
  useSlowConnection();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { tabs, activeTabId, setActiveTabId, openOrFocus, close } =
    usePatientTabs();

  // PHA-773: in-progress onboarding patient identity, reported up from
  // OnboardingWorkflow so the Onboarding tab shows the patient's name/MRN.
  const [onboardingIdentity, setOnboardingIdentity] = useState<{
    firstName: string;
    lastName: string;
    mrn: string;
  } | null>(null);
  const handleOnboardingIdentityChange = useCallback(
    (identity: { firstName: string; lastName: string; mrn: string }) => {
      setOnboardingIdentity(identity);
    },
    []
  );
  const onboardingName = onboardingIdentity
    ? [onboardingIdentity.firstName, onboardingIdentity.lastName].filter(Boolean).join(' ')
    : '';
  const onboardingTabLabel =
    onboardingName || onboardingIdentity?.mrn
      ? `Onboarding — ${onboardingName || onboardingIdentity?.mrn}`
      : 'Onboarding';
  const onboardingTabSubtitle = onboardingIdentity?.mrn
    ? `MRN ${onboardingIdentity.mrn}`
    : undefined;

  // PHA-895: guard leaving the Onboarding tab with unsaved care-plan edits. OnboardingWorkflow
  // (→ CarePlanApproval) reports whether edits differ from the session-entry baseline and
  // registers Save/Discard handlers the exit modal drives.
  const [onboardingUnsaved, setOnboardingUnsaved] = useState(false);
  const onboardingDraftControlsRef = useRef<{
    save: () => Promise<void>;
    discard: () => Promise<void>;
  } | null>(null);
  const setOnboardingDraftControls = useCallback(
    (controls: { save: () => Promise<void>; discard: () => Promise<void> } | null) => {
      onboardingDraftControlsRef.current = controls;
    },
    []
  );
  // When set, the Save/Discard modal is open; `proceed` runs the deferred tab switch.
  const [exitPrompt, setExitPrompt] = useState<{ proceed: () => void } | null>(null);
  const [exitBusy, setExitBusy] = useState(false);

  // Intercept a switch AWAY from onboarding while there are unsaved edits.
  const guardOnboardingExit = useCallback(
    (proceed: () => void) => {
      if (activeTabId === 'onboarding' && onboardingUnsaved) {
        setExitPrompt({ proceed });
      } else {
        proceed();
      }
    },
    [activeTabId, onboardingUnsaved]
  );
  const guardedOpenOrFocus = useCallback(
    (args: Parameters<typeof openOrFocus>[0]) => {
      guardOnboardingExit(() => openOrFocus(args));
    },
    [guardOnboardingExit, openOrFocus]
  );
  const finishExit = useCallback(() => {
    const proceed = exitPrompt?.proceed;
    setExitPrompt(null);
    setExitBusy(false);
    proceed?.();
  }, [exitPrompt]);
  const handleSaveAndLeave = useCallback(async () => {
    setExitBusy(true);
    try {
      await onboardingDraftControlsRef.current?.save();
    } catch {
      toast.error('Could not save the draft. Please try again.');
      setExitBusy(false);
      return;
    }
    finishExit();
  }, [finishExit]);
  const handleDiscardAndLeave = useCallback(async () => {
    setExitBusy(true);
    try {
      await onboardingDraftControlsRef.current?.discard();
    } catch {
      // Discard is best-effort — leave regardless so the user is never trapped.
    }
    finishExit();
  }, [finishExit]);

  // PHA-967: onboarding hands the patient over after a draft save or an approval. Open (or
  // re-focus) their chart on the Care Plans tab so the plan they just produced is the first thing
  // they see — previously this meant patient list → search → open, or an outright dead end.
  //
  // Uses the RAW openOrFocus: the wizard has already saved (or approved), so the unsaved-changes
  // guard has nothing left to protect and `onboardingUnsaved` may not have re-rendered to false
  // yet. Clearing it here rather than relying on that ordering is the point.
  const handleExitOnboardingToChart = useCallback(
    (patient: {
      patientId: string;
      firstName?: string;
      lastName?: string;
      mrn?: string | null;
    }) => {
      setOnboardingUnsaved(false);
      onboardingDraftControlsRef.current = null;
      // Drop ?tab=onboarding (and any _resume nonce) so a refresh doesn't reopen the wizard.
      // openOrFocus below sets the active tab to the patient in the same batch, so the
      // searchParams effect sees a non-onboarding active tab and leaves it alone.
      setSearchParams({}, { replace: true });
      openOrFocus({
        ...patient,
        initialTab: PATIENT_DETAIL_TAB_INDEX.CARE_PLANS,
      });
    },
    [openOrFocus, setSearchParams]
  );

  // PHA-1166: closing patient tabs on a clinic switch lives in PatientTabsProvider,
  // which stays mounted on every route (this component does not).

  // PHA-1168: Reports show for any user whose org has reports on (Phanova staff always,
  // including "All organizations"), not only clinic admins.
  const reportsAccess = useCustomViewsAccess(currentOrgId, 'report');

  // PHA-219: keep this rule in sync with BillingAccessRoute in App.tsx.
  const canAccessBilling =
    user?.is_internal ||
    user?.is_clinic_admin ||
    user?.roles?.includes('billing_operations_manager') ||
    user?.roles?.includes('clinical_medical_director');

  // URL ↔ active tab sync (only the Onboarding "?tab=onboarding" case is
  // deep-linkable; patient tabs are intentionally ephemeral per PHA-509).
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam === 'onboarding') {
      setActiveTabId('onboarding');
      window.scrollTo(0, 0);
    } else if (activeTabId === 'onboarding') {
      // URL no longer asks for onboarding but we're still on it — fall back.
      setActiveTabId('patients');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // PHA-509: Smart Hub AckDrawer (and any future caller) dispatches
  // `phanova:open-patient-detail` to surface a patient. The listener used to
  // live in PatientList; it now lives here so the Dashboard tab manager can
  // open a tab even when PatientList isn't mounted.
  useEffect(() => {
    const handler = (e: Event) => {
      const ce = e as CustomEvent<{ patientId: string; initialTab?: number }>;
      const patientId = ce.detail?.patientId;
      if (!patientId) return;
      guardedOpenOrFocus({ patientId, initialTab: ce.detail?.initialTab });
    };
    window.addEventListener('phanova:open-patient-detail', handler);
    return () => window.removeEventListener('phanova:open-patient-detail', handler);
  }, [guardedOpenOrFocus]);

  // PHA-387: ?openPatientId=<mrn> deep-link from outside the Dashboard tree
  // (e.g. the admin Eligibility tab). Open or focus the patient tab, then
  // strip the param so a refresh doesn't re-trigger.
  useEffect(() => {
    const id = searchParams.get('openPatientId');
    if (!id) return;
    guardedOpenOrFocus({ patientId: id });
    const next = new URLSearchParams(searchParams);
    next.delete('openPatientId');
    setSearchParams(next, { replace: true });
  }, [searchParams, guardedOpenOrFocus, setSearchParams]);

  const goToPatientList = () => {
    guardOnboardingExit(() => {
      setActiveTabId('patients');
      if (searchParams.get('tab')) {
        setSearchParams({});
      }
    });
  };

  // `fresh` forces a remount (same `_resume` nonce trick PHA-797 uses for Continue
  // Onboarding/Regenerate) so "Add Patient" always opens a blank wizard, even when the
  // Onboarding tab is already mounted mid-flow (e.g. a regenerate/cancel that errored out
  // and never reached exitToPatientChart — PHA-1065). The plain tab-click path (re-clicking
  // the already-active Onboarding tab) must NOT force this, or it would discard in-progress
  // onboarding work — the exact loss PHA-895 kept this tab permanently mounted to prevent.
  const goToOnboarding = (fresh = false) => {
    setActiveTabId('onboarding');
    setSearchParams(fresh ? { tab: 'onboarding', _resume: String(Date.now()) } : { tab: 'onboarding' });
  };

  // PHA-1141: the Smart Hub's "Re-generate care plan" on an EHR change. The same hand-off as
  // the chart's Regenerate (patient + source in sessionStorage, then a fresh wizard mount),
  // started here because the Smart Hub renders outside any route of its own. Guarded like every
  // other exit from an onboarding session with unsaved edits.
  useEffect(() => {
    const handler = (e: Event) => {
      const ce = e as CustomEvent<{ patientId: string; source?: string }>;
      const patientId = ce.detail?.patientId;
      if (!patientId) return;
      guardOnboardingExit(() => {
        sessionStorage.setItem(REGENERATE_PATIENT_ID_STORAGE_KEY, patientId);
        sessionStorage.setItem(REGENERATE_SOURCE_STORAGE_KEY, ce.detail?.source || 'current');
        goToOnboarding(true);
      });
    };
    window.addEventListener('phanova:regenerate-care-plan', handler);
    return () => window.removeEventListener('phanova:regenerate-care-plan', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guardOnboardingExit]);

  const goToPatientTab = (id: DashboardTabId) => {
    guardOnboardingExit(() => {
      setActiveTabId(id);
      if (searchParams.get('tab')) {
        setSearchParams({});
      }
    });
  };

  const handleLogout = async () => {
    try {
      await logout();
      toast.success('Logged out successfully');
    } catch (error) {
      toast.error('Failed to logout');
    }
  };

  const handleNewPatient = () => {
    if (currentOrgId === 'ALL') {
      setOrgPickerOpen(true);
      return;
    }
    guardOnboardingExit(() => goToOnboarding(true));
  };

  const handleOrgPickAndProceed = async (orgId: string) => {
    setOrgPickerOpen(false);
    try {
      await setOrg(orgId);
      guardOnboardingExit(() => goToOnboarding(true));
    } catch {
      toast.error('Failed to set organization');
    }
  };

  // Smart Hub patient awareness: if the active tab is a patient tab, look up
  // the underlying patient_id to drive the patient-context feed.
  const activePatientTab = tabs.find((t) => t.patientId === activeTabId);
  const smartHubPatientId = activePatientTab?.patientId;
  const smartHubPatientName = activePatientTab
    ? [activePatientTab.firstName, activePatientTab.lastName]
        .filter(Boolean)
        .join(' ') ||
      activePatientTab.patientId
    : undefined;

  const tabBaseStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: '10px 14px',
    border: 'none',
    background: 'transparent',
    borderBottom: '2px solid transparent',
    cursor: 'pointer',
    color: 'var(--slate-11)',
    fontSize: 14,
    fontWeight: 500,
    whiteSpace: 'nowrap',
    maxWidth: 240,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  };
  const activeTabStyle: React.CSSProperties = {
    color: 'var(--accent-11)',
    borderBottom: '2px solid var(--accent-9)',
    background: 'var(--gray-a2)',
  };

  return (
    <Box className="min-h-screen bg-slate-1">
      {/* Header */}
      <Box className="border-b border-slate-6 bg-white sticky top-0 z-50 shadow-sm">
        <Box style={{ maxWidth: 1536, marginLeft: 'auto', marginRight: 'auto', paddingLeft: 24, paddingRight: 24 }}>
          <Flex align="center" justify="between" py="4">
            <Flex align="center" gap="3">
              <img
                src="/logo192.png"
                alt="Phanova Logo"
                width="32"
                height="32"
              />
              <Heading size="6" className="text-slate-800">
                Phanova Continuous Care
              </Heading>
            </Flex>

            <Flex align="center" gap="3">
              <OrganizationSelector />
              <Popover.Root open={orgPickerOpen} onOpenChange={setOrgPickerOpen}>
                <Popover.Trigger onClick={(e: React.MouseEvent) => { e.preventDefault(); handleNewPatient(); }}>
                  <Button
                    size="3"
                    className="bg-blue-600 hover:bg-blue-700 cursor-pointer"
                  >
                    <PlusIcon className="mr-2" />
                    Add Patient
                  </Button>
                </Popover.Trigger>
                <Popover.Content side="bottom" align="end" style={{ width: 280 }}>
                  <Callout.Root color="amber" size="1" style={{ marginBottom: 12 }}>
                    <Callout.Icon>
                      <InfoCircledIcon />
                    </Callout.Icon>
                    <Callout.Text>
                      Select an organization for this patient
                    </Callout.Text>
                  </Callout.Root>
                  <Flex direction="column" gap="2">
                    {/* PHA-527: filter out the "ALL" pseudo-org — onboarding
                        needs a specific clinic to create a patient against. */}
                    {orgList.filter((org) => org.id !== 'ALL').map((org) => (
                      <Button
                        key={org.id}
                        variant="soft"
                        size="2"
                        className="cursor-pointer"
                        style={{ justifyContent: 'flex-start' }}
                        onClick={() => handleOrgPickAndProceed(org.id)}
                      >
                        {org.name}
                      </Button>
                    ))}
                  </Flex>
                </Popover.Content>
              </Popover.Root>

              <Separator orientation="vertical" size="2" />

              <DropdownMenu.Root>
                <DropdownMenu.Trigger>
                  <IconButton variant="ghost" size="3" className="cursor-pointer" radius="full">
                    <Avatar
                      size="2"
                      radius="full"
                      fallback={user?.name?.[0] || user?.email?.[0] || 'U'}
                    />
                  </IconButton>
                </DropdownMenu.Trigger>
                <DropdownMenu.Content align="end" sideOffset={8}>
                  <DropdownMenu.Label>
                    <Text size="2" weight="medium">{user?.name || user?.email || 'User'}</Text>
                  </DropdownMenu.Label>
                  <DropdownMenu.Separator />
                  {reportsAccess.enabled && (
                    <DropdownMenu.Item className="cursor-pointer" onClick={() => navigate('/reports')}>
                      <FileTextIcon className="mr-2" />
                      Reports
                    </DropdownMenu.Item>
                  )}
                  {canAccessBilling && (
                    <DropdownMenu.Item className="cursor-pointer" onClick={() => navigate('/billing')}>
                      <ClockIcon className="mr-2" />
                      Billing Report
                    </DropdownMenu.Item>
                  )}
                  {!isClinicAdmin && !user?.is_internal && (
                    <DropdownMenu.Item className="cursor-pointer" onClick={() => navigate('/clinic-settings/devices')}>
                      <ComponentPlaceholderIcon className="mr-2" />
                      Devices
                    </DropdownMenu.Item>
                  )}
                  {user?.is_internal && (
                    <DropdownMenu.Item className="cursor-pointer" onClick={() => navigate('/admin')}>
                      <DashboardIcon className="mr-2" />
                      Admin
                    </DropdownMenu.Item>
                  )}
                  {isClinicAdmin && (
                    <DropdownMenu.Item className="cursor-pointer" onClick={() => navigate('/clinic-settings')}>
                      <GearIcon className="mr-2" />
                      Clinic Settings
                    </DropdownMenu.Item>
                  )}
                  <DropdownMenu.Separator />
                  <DropdownMenu.Item className="cursor-pointer" onClick={handleLogout} color="red">
                    <ExitIcon className="mr-2" />
                    Logout
                  </DropdownMenu.Item>
                </DropdownMenu.Content>
              </DropdownMenu.Root>
            </Flex>
          </Flex>
        </Box>
      </Box>

      {/* Main Content */}
      <Box
        className="py-6"
        style={{ maxWidth: 1536, marginLeft: 'auto', marginRight: 'auto', paddingLeft: 24, paddingRight: 24 }}
      >
        <Flex
          gap="4"
          direction={{ initial: 'column', lg: 'row' }}
          align="stretch"
        >
          <Box style={{ flex: 3, minWidth: 0, width: '100%', display: 'flex', flexDirection: 'column' }}>
            <Card
              className={activePatientTab ? 'shadow-sm patient-chart-card' : 'shadow-sm'}
              style={{ flex: 1, padding: 0 }}
            >
              {/* Dynamic tab strip — PHA-543: sticky directly under the app
                  header *only* on patient-chart tabs so the patient name stays
                  in view while the chart scrolls. Patient List / Onboarding
                  views keep the original (non-sticky) strip to avoid
                  interactions with the PatientList's internal sticky table
                  header + viewport-height math. */}
              <Flex
                align="center"
                style={{
                  borderBottom: '1px solid var(--slate-5)',
                  overflowX: 'auto',
                  flexWrap: 'nowrap',
                  ...(activePatientTab
                    ? {
                        position: 'sticky',
                        top: APP_HEADER_HEIGHT,
                        background: 'white',
                        zIndex: 40,
                      }
                    : null),
                }}
              >
                <button
                  type="button"
                  onClick={goToPatientList}
                  style={{
                    ...tabBaseStyle,
                    ...(activeTabId === 'patients' ? activeTabStyle : null),
                  }}
                >
                  <DashboardIcon width="16" height="16" />
                  Patient List
                </button>

                {activeTabId === 'onboarding' && (
                  <Tooltip
                    content={onboardingTabSubtitle ?? 'Onboarding'}
                    delayDuration={250}
                  >
                    <button
                      type="button"
                      onClick={() => goToOnboarding()}
                      style={{
                        ...tabBaseStyle,
                        ...activeTabStyle,
                        maxWidth: 240,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      aria-label={onboardingTabLabel}
                    >
                      <PlusIcon width="16" height="16" />
                      <span
                        style={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {onboardingTabLabel}
                      </span>
                    </button>
                  </Tooltip>
                )}

                {tabs.map((t) => {
                  const label =
                    [t.firstName, t.lastName].filter(Boolean).join(' ') ||
                    t.patientId;
                  const subtitle = t.mrn ? `MRN ${t.mrn}` : t.patientId;
                  const isActive = activeTabId === t.patientId;
                  return (
                    <Tooltip key={t.patientId} content={subtitle} delayDuration={250}>
                      <Flex
                        align="center"
                        style={{
                          ...tabBaseStyle,
                          ...(isActive ? activeTabStyle : null),
                          paddingRight: 8,
                          gap: 4,
                        }}
                      >
                        <button
                          type="button"
                          onClick={() => goToPatientTab(t.patientId)}
                          style={{
                            background: 'transparent',
                            border: 'none',
                            padding: 0,
                            cursor: 'pointer',
                            color: 'inherit',
                            font: 'inherit',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 6,
                            maxWidth: 200,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                          aria-label={`Open ${label}`}
                        >
                          <PersonIcon width="14" height="14" />
                          <span
                            style={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {label}
                          </span>
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            close(t.patientId);
                          }}
                          aria-label={`Close ${label} tab`}
                          style={{
                            background: 'transparent',
                            border: 'none',
                            padding: 4,
                            marginLeft: 2,
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            color: 'var(--slate-9)',
                            borderRadius: 4,
                          }}
                        >
                          <Cross2Icon width="12" height="12" />
                        </button>
                      </Flex>
                    </Tooltip>
                  );
                })}
              </Flex>

              {/* Tab bodies — all rendered, only the active one visible.
                  Keeps already-loaded patient state (inner-tab selection,
                  fetched data) intact across switches per PHA-509 AC #8. */}
              <Box style={{ padding: 24 }}>
                <Box style={{ display: activeTabId === 'patients' ? 'block' : 'none' }}>
                  <PatientList />
                </Box>
                {/* PHA-895: keep the Onboarding tab MOUNTED (display:none when inactive) like the
                    other tabs, so switching tabs and back no longer unmounts OnboardingWorkflow and
                    silently discards in-progress care-plan edits. The draft autosaves and prompts
                    Save/Discard on exit, but preserving the live component is what makes tab
                    round-trips lossless. */}
                <Box style={{ display: activeTabId === 'onboarding' ? 'block' : 'none' }}>
                  {/* PHA-797: key on _resume forces a fresh mount when "Continue Onboarding" is
                      clicked while the onboarding tab is already active. Without this, navigate
                      to '/?tab=onboarding' is a no-op (searchParams unchanged → useEffect never
                      fires → OnboardingWorkflow never remounts → sessionStorage patient ID never
                      consumed). The normal tab-switch path omits _resume so key stays '' and no
                      spurious remounts occur. */}
                  <OnboardingWorkflow
                    key={searchParams.get('_resume') ?? ''}
                    onPatientIdentityChange={handleOnboardingIdentityChange}
                    onUnsavedChangesChange={setOnboardingUnsaved}
                    registerDraftControls={setOnboardingDraftControls}
                    onExitToPatientChart={handleExitOnboardingToChart}
                  />
                </Box>
                {tabs.map((t) => (
                  <Box
                    key={t.patientId}
                    style={{ display: activeTabId === t.patientId ? 'block' : 'none' }}
                  >
                    <PatientTabContent
                      patientId={t.patientId}
                      initialTab={t.initialTab}
                      openNonce={t.openNonce}
                    />
                  </Box>
                ))}
              </Box>
            </Card>
          </Box>

          {/* Smart Hub rail — patient-aware when a patient tab is active.
              PHA-543: on a patient chart tab, pin the SmartHub to the viewport
              (the outer column still stretches so the right side doesn't
              collapse). On the Patient List view, keep the original behavior
              — sticky there would interact with PatientList's internal sticky
              table header and felt buggy. */}
          {activeTabId !== 'onboarding' && hideSmartHub === false && (
            <Box
              style={{
                flex: 1,
                minWidth: 0,
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {activePatientTab ? (
                <Box
                  style={{
                    position: 'sticky',
                    top: APP_HEADER_HEIGHT,
                    // Ends above the closed "Ask Phanova AI" pill, which sits
                    // at the bottom of this column, so the feed's last rows
                    // can be scrolled into view instead of under it. The 24px
                    // is the content's top padding: until the page scrolls the
                    // rail starts that far below the header.
                    maxHeight: `calc(100vh - ${APP_HEADER_HEIGHT}px - 24px - ${FLOATING_CHAT_CLEARANCE}px)`,
                    overflowY: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <SmartHubPanel
                    organizationId={currentOrgId}
                    patientId={smartHubPatientId}
                    patientName={smartHubPatientName}
                    fillHeight={false}
                  />
                </Box>
              ) : (
                <SmartHubPanel
                  organizationId={currentOrgId}
                  patientId={smartHubPatientId}
                  patientName={smartHubPatientName}
                />
              )}
            </Box>
          )}
        </Flex>
      </Box>

      {/* PHA-895: Save/Discard prompt shown when leaving the Onboarding tab with unsaved edits. */}
      <AlertDialog.Root
        open={!!exitPrompt}
        onOpenChange={(open) => {
          if (!open && !exitBusy) setExitPrompt(null);
        }}
      >
        <AlertDialog.Content style={{ maxWidth: 460 }}>
          <AlertDialog.Title>Unsaved changes</AlertDialog.Title>
          <AlertDialog.Description size="2">
            You have unsaved changes to this care plan draft. Save them before leaving, or discard
            to revert to the version that was loaded when you opened it.
          </AlertDialog.Description>
          <Flex gap="3" mt="4" justify="end">
            <Button
              variant="soft"
              color="gray"
              disabled={exitBusy}
              onClick={() => setExitPrompt(null)}
            >
              Cancel
            </Button>
            <Button variant="soft" color="red" disabled={exitBusy} onClick={handleDiscardAndLeave}>
              Discard
            </Button>
            <Button disabled={exitBusy} onClick={handleSaveAndLeave}>
              {exitBusy ? 'Saving…' : 'Save'}
            </Button>
          </Flex>
        </AlertDialog.Content>
      </AlertDialog.Root>

      {/* PHA-1071: FloatingChat now mounts once in App.tsx, above <Routes>, so
          the conversation survives navigation and is available on every screen.
          Mounting it here as well would render a second, independent panel. */}
    </Box>
  );
};

// PHA-1071: PatientTabsProvider moved up to AuthenticatedApp so the agent
// panel — which now lives above <Routes> — shares the same tab context and can
// still tell which patient the clinician has open. Keeping the provider here
// would have left useOptionalPatientTabs() returning null for the chat, quietly
// collapsing every conversation to clinic scope and disabling the PHA-549
// patient briefing. The ?tab=onboarding deep link still works: the provider
// reads it on mount, and DashboardLayout's searchParams effect handles
// navigation to it afterwards.
const Dashboard: React.FC = () => <DashboardLayout />;

export default Dashboard;
